import {WriteButton} from '../common/buttons/WriteActions';
import {useEffect, useState} from 'react';
import {Alert, Box, InputAdornment, Snackbar, TextField} from '@mui/material';
import {getMedia} from '../../common/mediaQuery';
import {getStyles} from '../items/tableCommon';
import {fetchCustomers} from './dataManager';
import {ICustomerGuid} from '@mybake/ui/models/Customers/ICustomerGuid';
import {useAppTranslation} from "@mybake/ui/hooks/useAppTranslation";
import {CustomerTable} from './customer/CustomerTable';
import {useNavigate} from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import {Clear} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import useDebounce from '../../hooks/useDebounce';
import {usePageLimit} from '../../hooks/usePageLimit';
import {getApiErrorKind, getApiErrorMessage, getApiTraceId} from '../../common/apiErrors';
import TraceIdFeedback from '../feedback/TraceIdFeedback';
import {PageHeader} from '../common/layout/PageHeader';
import {Surface} from '../common/layout/Surface';
import {ContentSkeleton} from '../common/loading/ContentSkeleton';

// TODO: need to fix order profit in create order component

const Customers = () => {
    const {t} = useAppTranslation();
    const {isFullHD} = getMedia();
    const {wrapperItems, boxItemsLoading} = getStyles();
    const navigate = useNavigate();

    const [customersData, setCustomersData] = useState<ICustomerGuid[]>([]);
    const [currentPageNumber, setCurrentPageNumber] = useState(0);
    const [itemsPerPage, setItemsPerPage] = usePageLimit('customers');
    const [itemsQtyTotal, setItemsQtyTotal] = useState(1);
    const [searchPhrase, setSearchPhrase] = useState('');
    const debouncedSearchPhrase = useDebounce(searchPhrase, 600);

    const [orderByName, setOrderByName] = useState(true);
    const [orderByOrdersQty, setByOrderQty] = useState(false);
    const [descending, setDescending] = useState(false);
    const [serverToast, setServerToast] = useState('');
    const [traceId, setTraceId] = useState('');

    const {customers, getAllCustomersIsLoading, getAllCustomersError, getAllCustomersIsSuccess} = fetchCustomers({
        currentPageNumber,
        itemsPerPage,
        searchPhrase: debouncedSearchPhrase.trim().length >= 3 ? debouncedSearchPhrase.trim() : '',
        descending,
        orderByName,
        orderByOrdersQty,
    });

    useEffect(() => {
        if (itemsPerPage < 1 || itemsPerPage > 200) setItemsPerPage(15);
    }, [itemsPerPage, setItemsPerPage]);

    useEffect(() => {
        if (!getAllCustomersError || getApiErrorKind(getAllCustomersError) !== 'server') return;
        setServerToast(getApiErrorMessage(getAllCustomersError, t));
        setTraceId(getApiTraceId(getAllCustomersError));
    }, [getAllCustomersError, t]);

    useEffect(() => {
        if (customers) {
            setCustomersData(customers.data);
            setCurrentPageNumber(customers.page);
            setItemsPerPage(customers.pageSize);
            setItemsQtyTotal(customers.totalCount);

        }
    }, [customers]);

    const handleSortRequest = (id: 'name', direction: 'asc' | 'desc') => {
        setDescending(direction === 'desc');
        setOrderByName(id === 'name');
        //setByOrderQty(id === 'orders');
    };

    return (
        <Box sx={{...wrapperItems, p: 0, ...(isFullHD ? {minHeight: '830px'} : {})}}>
            <PageHeader title={t('customers:customers')} action={<WriteButton variant="contained" startIcon={<AddIcon/>} onClick={() => navigate('new')}>
                    {t('customers:create_customer')}
                </WriteButton>}/>

            {getAllCustomersIsLoading && (
                <Box sx={boxItemsLoading}>
                    <ContentSkeleton variant="list"/>
                </Box>
            )}
            {getAllCustomersError && getApiErrorKind(getAllCustomersError) !== 'server' && <Alert severity="error">{getApiErrorMessage(getAllCustomersError, t)}</Alert>}

            {getAllCustomersIsSuccess && !getAllCustomersIsLoading && !getAllCustomersError && (
                <Surface sx={{width: '100%'}}>
                    <Box sx={{p: 2}}>
                        <TextField
                            label={t('customers:search_customer')}
                            variant="outlined"
                            focused
                            size="small"
                            fullWidth
                            value={searchPhrase}
                            onChange={(event) => setSearchPhrase(event.target.value)}
                            InputProps={{
                                endAdornment: searchPhrase ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchPhrase('')}>
                                            <Clear/>
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                    </Box>
                    <CustomerTable
                        data={customersData}
                        totalCount={itemsQtyTotal}
                        pageSize={itemsPerPage}
                        currentPage={currentPageNumber}
                        descending={descending}
                        orderByName={orderByName}
                        orderByOrdersQty={orderByOrdersQty}
                        onPageChange={setCurrentPageNumber}
                        onPageSizeChange={(size) => {
                            setItemsPerPage(size);
                            setCurrentPageNumber(0);
                        }}
                        onSortRequest={handleSortRequest}
                    />
                </Surface>
            )}
            <Snackbar open={!!serverToast} autoHideDuration={6000} onClose={() => setServerToast('')}>
                <Alert severity="error" onClose={() => setServerToast('')}>{serverToast}<TraceIdFeedback traceId={traceId}/></Alert>
            </Snackbar>
        </Box>
    );
};

export default Customers;
